import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Star, Calendar, Clock, CheckCircle, Loader2, LogOut, Shield,
  LayoutGrid, ClipboardList, Wallet, User, MapPin, Phone, IndianRupee, Upload,
  AlertCircle, RefreshCw, PlayCircle, Send, Image as ImageIcon, Power,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AccountSecurityCard from "../components/AccountSecurityCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const axi = axios.create({ baseURL: API_URL, withCredentials: true });

/* ───────────────────────── Login (unchanged, secure OTP) ───────────────────────── */
const VendorLogin = ({ onLogin }) => {
  const [phase, setPhase] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sendOtp = async () => {
    if (!phone || phone.length < 10) return setError("Enter a valid phone");
    setLoading(true); setError("");
    try { await axi.post("/api/vendors/auth/send-otp", { phone, purpose: "login" }); setPhase("otp"); }
    catch (e) { setError(e?.response?.data?.detail || "Failed to send OTP"); }
    setLoading(false);
  };
  const verifyOtp = async () => {
    if (!code || code.length < 4) return setError("Enter the OTP");
    setLoading(true); setError("");
    try { const r = await axi.post("/api/vendors/auth/verify-otp", { phone, code, purpose: "login" }); onLogin(r.data.vendor); }
    catch (e) { setError(e?.response?.data?.detail || "OTP verification failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-8" data-testid="vendor-login">
        <div className="text-center mb-6">
          <Briefcase size={32} className="text-[#E83E8C] mx-auto mb-2" />
          <h1 className="text-xl font-bold text-[#1E1B4B]">Vendor Portal</h1>
          <p className="text-xs text-[#94a3b8] flex items-center justify-center gap-1 mt-1"><Shield size={11} /> Secure OTP Login</p>
        </div>
        {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2 mb-3" data-testid="vendor-login-error">{error}</p>}
        {phase === "phone" ? (
          <div className="space-y-3">
            <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ""))} placeholder="Registered Phone Number"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="vendor-phone-input" />
            <button onClick={sendOtp} disabled={loading || phone.length < 10}
              className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-medium text-sm disabled:opacity-50 hover:bg-[#d1356f]" data-testid="vendor-send-otp-btn">
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">OTP sent to <b>{phone}</b></p>
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit OTP"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-center text-lg font-mono tracking-[0.4em]" data-testid="vendor-otp-input" />
            <button onClick={verifyOtp} disabled={loading || code.length < 4}
              className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-medium text-sm disabled:opacity-50 hover:bg-[#d1356f]" data-testid="vendor-verify-otp-btn">
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Verify & Login"}
            </button>
            <button onClick={() => { setPhase("phone"); setCode(""); setError(""); }} className="w-full text-xs text-gray-500 hover:text-[#E83E8C]" data-testid="vendor-change-phone-btn">← Change number</button>
          </div>
        )}
        <div className="text-center mt-4"><Link to="/vendor-register" className="text-xs text-[#E83E8C] hover:underline">New vendor? Register here</Link></div>
      </div>
    </div>
  );
};

/* ───────────────────────── Helpers ───────────────────────── */
const STATUS_TABS = [
  { key: "assigned", label: "New" },
  { key: "accepted", label: "Accepted" },
  { key: "in_progress", label: "In Progress" },
  { key: "submitted", label: "Submitted" },
  { key: "completed", label: "Completed" },
];
const STATUS_BADGE = {
  assigned: "bg-blue-50 text-blue-700",
  accepted: "bg-emerald-50 text-emerald-700",
  in_progress: "bg-amber-50 text-amber-700",
  submitted: "bg-purple-50 text-purple-700",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-50 text-red-600",
};
const CATEGORY_HINT = {
  "Balloon shop": "Balloon & decoration setup",
  "Balloon Art": "Balloon art / decoration",
  "Cake & Bakery": "Cake / bakery order",
  "Photography": "Event photography coverage",
  "Catering": "Catering order",
  "DJ & Music": "DJ / music setup",
  "Lighting": "Lighting setup",
  "Props & Rentals": "Props & rentals",
  "Anchor / Host": "Anchoring / hosting",
  "Magician / Entertainer": "Entertainment act",
};

const KpiCard = ({ icon: Icon, label, value, sub, tint = "#1E1B4B", testid }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} style={{ color: "#E83E8C" }} />
      <span className="text-[9px] font-semibold text-[#94a3b8] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold" style={{ color: tint }}>{value}</p>
    {sub && <p className="text-[10px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

/* ───────────────────────── Assignment card ───────────────────────── */
const AssignmentCard = ({ a, busy, onAct, onUpload }) => {
  const badge = STATUS_BADGE[a.status] || "bg-gray-100 text-gray-600";
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" data-testid={`assignment-${a.assignment_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#1E1B4B]">{a.occasion || "Party"} · {a.booking_id}</p>
          <p className="text-[11px] text-[#7C3AED] font-semibold">{CATEGORY_HINT[a.service_type] || a.service_type}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase ${badge}`}>{String(a.status).replace(/_/g, " ")}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-[11px] text-[#475569]">
        <span className="flex items-center gap-1.5"><Calendar size={12} className="text-[#94a3b8]" /> {a.date}</span>
        <span className="flex items-center gap-1.5"><Clock size={12} className="text-[#94a3b8]" /> {a.setup_time || "—"}</span>
        <span className="flex items-center gap-1.5 col-span-2"><MapPin size={12} className="text-[#94a3b8]" /> {a.area ? `${a.area} · ` : ""}Pincode {a.pincode || "—"}</span>
        {a.ops_contact?.name && <span className="flex items-center gap-1.5 col-span-2"><Phone size={12} className="text-[#94a3b8]" /> OPS: {a.ops_contact.name} {a.ops_contact.phone}</span>}
        {a.add_ons?.length > 0 && <span className="col-span-2 text-[10px] text-[#94a3b8]">Requested: {a.add_ons.join(", ")}</span>}
        {a.notes && <span className="col-span-2 text-[10px] text-[#475569] bg-[#FFF7FB] rounded px-2 py-1">{a.notes}</span>}
      </div>

      {a.evidence?.length > 0 && (
        <div className="flex gap-1.5 mt-2" data-testid={`evidence-${a.assignment_id}`}>
          {a.evidence.map((e) => (
            <span key={e.evidence_id} className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 rounded px-2 py-1">
              <ImageIcon size={11} /> photo
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {a.status === "assigned" && (
          <button disabled={busy} onClick={() => onAct(a, "accept")} data-testid={`accept-${a.assignment_id}`}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#10B981] text-white rounded-lg text-[11px] font-bold hover:bg-[#059669] disabled:opacity-40">
            <CheckCircle size={12} /> Accept Assignment
          </button>
        )}
        {a.status === "accepted" && (
          <button disabled={busy} onClick={() => onAct(a, "in_progress")} data-testid={`start-${a.assignment_id}`}
            className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-[11px] font-bold hover:bg-amber-600 disabled:opacity-40">
            <PlayCircle size={12} /> Start Job
          </button>
        )}
        {a.status === "in_progress" && (
          <>
            <label className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-[11px] font-bold text-[#475569] hover:bg-gray-50 cursor-pointer" data-testid={`upload-${a.assignment_id}`}>
              <Upload size={12} /> Add Photo
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => e.target.files?.[0] && onUpload(a, e.target.files[0])} />
            </label>
            <button disabled={busy} onClick={() => onAct(a, "submitted")} data-testid={`submit-${a.assignment_id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#7C3AED] text-white rounded-lg text-[11px] font-bold hover:bg-[#6D28D9] disabled:opacity-40">
              <Send size={12} /> Mark Completed
            </button>
          </>
        )}
        {a.status === "submitted" && <span className="text-[11px] text-[#7C3AED] font-semibold flex items-center gap-1"><Clock size={12} /> Awaiting OPS verification</span>}
        {a.status === "completed" && <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle size={12} /> Completed & verified</span>}
      </div>
    </div>
  );
};

/* ───────────────────────── Dashboard (tabbed) ───────────────────────── */
const VendorDashboard = ({ vendor: initialVendor, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [aLoading, setALoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("assigned");
  const [payments, setPayments] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(true);
  const [togglingAvail, setTogglingAvail] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try { const r = await axi.get("/api/vendors/portal/me/dashboard"); setData(r.data); }
    catch (e) { if (e.response?.status === 401) onLogout(); }
    setLoading(false);
  }, [onLogout]);

  const loadAssignments = useCallback(async (status) => {
    setALoading(true);
    try { const r = await axi.get("/api/vendors/portal/me/assignments", { params: status ? { status } : {} }); setAssignments(r.data.assignments || []); }
    catch { /* silent */ }
    setALoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { setAccepting(data?.vendor?.accepting_jobs !== false); }, [data?.vendor?.accepting_jobs]);
  useEffect(() => { if (tab === "assignments") loadAssignments(statusFilter); }, [tab, statusFilter, loadAssignments]);
  useEffect(() => { if (tab === "earnings" && !payments) axi.get("/api/vendors/portal/me/payments").then(r => setPayments(r.data)).catch(() => setPayments({ payments: [], total_received: 0 })); }, [tab, payments]);

  const toggleAvailability = async () => {
    const next = !accepting;
    setTogglingAvail(true);
    setAccepting(next);
    try {
      await axi.post("/api/vendors/portal/me/availability", { accepting_jobs: next });
      toast.success(next ? "You're now Active — OPS can assign you new jobs" : "You're now Inactive — you won't receive new jobs");
    } catch (e) {
      setAccepting(!next);
      toast.error(e.response?.data?.detail || "Could not update your status");
    }
    setTogglingAvail(false);
  };

  const act = async (a, status) => {
    setBusy(true);
    try {
      if (status === "accept") await axi.post("/api/vendors/portal/me/assignments/accept", { assignment_id: a.assignment_id });
      else await axi.post("/api/vendors/portal/me/assignments/status", { assignment_id: a.assignment_id, status });
      toast.success(status === "accept" ? "Assignment accepted" : status === "submitted" ? "Submitted for OPS verification" : "Status updated");
      await loadAssignments(statusFilter); await loadDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const upload = async (a, file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("assignment_id", a.assignment_id);
      fd.append("file", file);
      await axi.post("/api/vendors/portal/me/assignments/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      await loadAssignments(statusFilter);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FFFAF5]"><Loader2 className="animate-spin text-[#E83E8C]" size={32} /></div>;

  const vendor = data?.vendor || initialVendor;
  const s = data?.stats || {};
  const qs = s.quality_score ?? null;
  const tier = qs == null ? "New Partner" : qs >= 80 ? "Gold" : qs >= 60 ? "Silver" : qs >= 40 ? "Bronze" : "Starter";
  const statusCls = vendor.status === "approved" ? "bg-emerald-400/20 text-emerald-200"
    : (vendor.status === "suspended" || vendor.status === "rejected") ? "bg-red-400/20 text-red-200" : "bg-amber-400/20 text-amber-200";

  const TABS = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "assignments", label: "Assignments", icon: ClipboardList },
    { key: "earnings", label: "Earnings", icon: Wallet },
    { key: "profile", label: "Business Profile", icon: User },
    { key: "security", label: "Account Security", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="vendor-dashboard">
      {/* Compact header */}
      <div className="bg-gradient-to-r from-[#1E1B4B] to-indigo-800 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-white rounded-lg px-2 py-1.5 flex items-center justify-center ring-1 ring-white/20 shadow-sm flex-shrink-0">
              <img src="/doozygo-logo.png" alt="DoozyGo®" className="h-6 w-auto" data-testid="vendor-header-logo" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-white truncate">{vendor.name}</h1>
              <p className="text-white/60 text-[11px] mt-0.5 truncate">{vendor.service_type} · {vendor.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${statusCls}`}>{vendor.status}</span>
              <p className="text-white/40 text-[9px] mt-0.5">{tier}</p>
            </div>
            <button onClick={onLogout} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="vendor-logout-btn" title="Logout"><LogOut size={14} /></button>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-2 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`vtab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-[#E83E8C] text-[#E83E8C]" : "border-transparent text-[#64748B] hover:text-[#1E1B4B]"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-5">
        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            {/* Availability toggle */}
            <div className={`rounded-2xl border p-4 flex items-center justify-between gap-4 ${accepting ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`} data-testid="availability-card">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${accepting ? "bg-emerald-500/15 text-emerald-600" : "bg-gray-400/15 text-gray-500"}`}>
                  <Power size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2">
                    You are {accepting ? "Active" : "Inactive"}
                    <span data-testid="availability-status" className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${accepting ? "bg-emerald-500 text-white" : "bg-gray-400 text-white"}`}>{accepting ? "Active" : "Inactive"}</span>
                  </p>
                  <p className="text-[11px] text-[#64748B] mt-0.5">{accepting ? "OPS can assign you new jobs." : "You won't receive new job assignments until you turn this back on."}</p>
                </div>
              </div>
              <button
                onClick={toggleAvailability}
                disabled={togglingAvail}
                data-testid="availability-toggle"
                role="switch"
                aria-checked={accepting}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${accepting ? "bg-emerald-500" : "bg-gray-300"}`}
                title={accepting ? "Turn inactive" : "Turn active"}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${accepting ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard testid="kpi-new" icon={ClipboardList} label="New Assignments" value={s.new_assignments ?? 0} tint="#3B82F6" />
              <KpiCard testid="kpi-active" icon={PlayCircle} label="Active Jobs" value={s.active_jobs ?? 0} tint="#F59E0B" />
              <KpiCard testid="kpi-completed" icon={CheckCircle} label="Completed Jobs" value={s.completed_jobs ?? 0} tint="#10B981" />
              <KpiCard testid="kpi-earnings" icon={IndianRupee} label="Total Earnings" value={`₹${(s.total_earned ?? 0).toLocaleString()}`} sub={`₹${(s.month_earned ?? 0).toLocaleString()} this month`} tint="#1E1B4B" />
              <KpiCard testid="kpi-rating" icon={Star} label="Rating" value={s.has_completed && s.rating ? s.rating : "—"} sub={s.has_completed ? `${s.total_jobs || 0} jobs` : "No completed jobs yet"} tint="#E83E8C" />
              <KpiCard testid="kpi-ontime" icon={Clock} label="On-Time Rate" value={s.on_time_rate == null ? "—" : `${s.on_time_rate}%`} sub={s.on_time_rate == null ? "No data yet" : ""} tint="#7C3AED" />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-[#1E1B4B]">Quality Score</p>
                <p className="text-[11px] text-[#94a3b8]">{qs == null ? "Builds up as you complete verified jobs" : `${tier} tier`}</p>
              </div>
              <span className="text-2xl font-bold text-[#E83E8C]">{qs == null ? "—" : qs}</span>
            </div>
            {s.new_assignments > 0 ? (
              <button onClick={() => { setTab("assignments"); setStatusFilter("assigned"); }} data-testid="overview-cta-new"
                className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-bold text-sm hover:bg-[#d1356f]">
                You have {s.new_assignments} new assignment(s) — review now
              </button>
            ) : (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center" data-testid="overview-empty">
                <ClipboardList size={28} className="text-[#cbd5e1] mx-auto mb-2" />
                <p className="text-sm font-semibold text-[#64748B]">No new assignments yet</p>
                <p className="text-xs text-[#94a3b8] mt-1">New jobs will appear here when OPS assigns work to your business.</p>
              </div>
            )}
          </>
        )}

        {/* ASSIGNMENTS */}
        {tab === "assignments" && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex gap-1 overflow-x-auto">
                {STATUS_TABS.map((st) => (
                  <button key={st.key} onClick={() => setStatusFilter(st.key)} data-testid={`astab-${st.key}`}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap ${statusFilter === st.key ? "bg-[#1E1B4B] text-white" : "bg-white border border-gray-200 text-[#64748B]"}`}>{st.label}</button>
                ))}
              </div>
              <button onClick={() => loadAssignments(statusFilter)} className="p-2 rounded-lg border border-gray-200 text-[#64748B] hover:bg-gray-50" data-testid="assignments-refresh"><RefreshCw size={13} /></button>
            </div>
            {aLoading ? (
              <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#E83E8C]" size={24} /></div>
            ) : assignments.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center" data-testid="assignments-empty">
                <ClipboardList size={26} className="text-[#cbd5e1] mx-auto mb-2" />
                <p className="text-sm text-[#64748B]">No {STATUS_TABS.find(x => x.key === statusFilter)?.label.toLowerCase()} assignments.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignments.map((a) => <AssignmentCard key={a.assignment_id} a={a} busy={busy} onAct={act} onUpload={upload} />)}
              </div>
            )}
          </>
        )}

        {/* EARNINGS */}
        {tab === "earnings" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <KpiCard testid="earn-total" icon={IndianRupee} label="Total Received" value={`₹${(payments?.total_received ?? 0).toLocaleString()}`} />
              <KpiCard testid="earn-month" icon={Calendar} label="This Month" value={`₹${(s.month_earned ?? 0).toLocaleString()}`} />
              <KpiCard testid="earn-pending" icon={AlertCircle} label="Pending Payments" value="—" sub="Payment tracking not configured" tint="#94a3b8" />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-sm font-bold text-[#1E1B4B] mb-3">Payment History</h3>
              {!payments ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto my-6" size={20} /> :
                payments.payments.length === 0 ? (
                  <p className="text-xs text-[#94a3b8] text-center py-4" data-testid="earnings-empty">No payments recorded yet. Payments appear here once DoozyGo settles a completed job.</p>
                ) : (
                  <ul className="divide-y divide-gray-50" data-testid="payment-history">
                    {payments.payments.map((p, i) => (
                      <li key={p.id || i} className="flex items-center justify-between py-2 text-xs">
                        <span className="text-[#475569]">{p.date} {p.notes ? `· ${p.notes}` : ""}</span>
                        <span className="font-bold text-emerald-600">₹{(p.amount || 0).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
            <p className="text-[10px] text-[#94a3b8]">Amounts reflect payments recorded by DoozyGo for your completed jobs, not customer booking totals.</p>
          </>
        )}

        {/* BUSINESS PROFILE */}
        {tab === "profile" && (
          <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="vendor-profile">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-4">Business Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Business name", vendor.name],
                ["Contact person", vendor.contact_person || vendor.owner_name || "—"],
                ["Phone", vendor.phone],
                ["Email", vendor.email || "—"],
                ["Service category", vendor.service_type],
                ["Vendor ID", vendor.id],
                ["Serviced PIN codes", (vendor.service_pincodes || []).join(", ") || "—"],
                ["Business address", (vendor.shop_location?.address) || vendor.address || "—"],
                ["Approval status", vendor.status],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">{k}</p>
                  <p className="text-[#1E1B4B] font-medium break-words">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#94a3b8] mt-4">To change your bank details or phone number, use Account Security — these are protected by OTP verification.</p>
          </div>
        )}

        {/* ACCOUNT SECURITY */}
        {tab === "security" && (
          <AccountSecurityCard authAxios={axi}
            onSessionRotated={() => { if (onLogout) onLogout(); else window.location.href = "/vendor-portal"; }}
            label="Vendor Account Security" />
        )}
      </div>
    </div>
  );
};

export default function VendorPortal() {
  const [vendor, setVendor] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axi.get("/api/vendors/auth/me").then(r => setVendor(r.data.vendor)).catch(() => setVendor(null)).finally(() => setChecking(false));
  }, []);

  const logout = async () => { try { await axi.post("/api/vendors/auth/logout"); } catch { /* ignore */ } setVendor(null); };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#FFFAF5]"><Loader2 className="animate-spin text-[#E83E8C]" size={32} /></div>;
  if (!vendor) return <VendorLogin onLogin={setVendor} />;
  return <VendorDashboard vendor={vendor} onLogout={logout} />;
}
